/**
 * @fileoverview Task / calendar export builder (MOR-1048).
 *
 * The calendar's first export: shapes the task surface's filtered rows into
 * the shared `ExportContent` contract, so the standard `ExportButton` (Word /
 * PDF / Excel and the custom export tool) works over tasks exactly as it
 * does over the data-agent registers.
 *
 * Used by:
 * - src/calendar/task-surface.tsx
 */

import { format, parseISO } from "date-fns";

import type { ExportContent } from "@/utils/export";
import { getDisplayName } from "@/ui/composed";
import { isEventKind, type CalendarEventRow } from "./eventAdapter";
import { typeBadgeMeta } from "./categoryMeta";
import { priorityMeta } from "./task-priority";
import type { TaskListLookups } from "./components/list/task-list-view";

function shortDate(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    return format(parseISO(iso), "d MMM yyyy");
  } catch {
    return iso;
  }
}

/**
 * Build the shared export content for a set of task rows. Column values are
 * the resolved display strings (status name, assignee name, priority label)
 * rather than ids, so the export reads like the on-screen list and the
 * custom export tool can group and filter on them.
 */
export function buildTasksExport(
  rows: CalendarEventRow[],
  lookups: TaskListLookups,
  { crossJob, title }: { crossJob: boolean; title?: string },
): ExportContent {
  const headers = [
    "Title",
    ...(crossJob ? ["Job"] : []),
    "Type",
    "Status",
    "Assignee",
    "Priority",
    "Due date",
    "Planned from",
    "Planned to",
    "Labels",
    "Overdue",
    "Subtasks done",
    "Subtasks total",
    "Notes",
  ];

  const bodyRows = rows.map((row) => {
    const status = row.statusId
      ? lookups.statusLookup.get(String(row.statusId))
      : undefined;
    const assignee = row.assigneeUserId
      ? lookups.memberById.get(row.assigneeUserId)
      : undefined;
    const labels = (row.labelIds ?? [])
      .map((id) => lookups.labelLookup.get(String(id))?.name)
      .filter(Boolean)
      .join("; ");
    return [
      row.title,
      ...(crossJob ? [row.jobName ?? ""] : []),
      typeBadgeMeta(isEventKind(row), row.category, Boolean(row.parentTaskId))
        .label,
      status?.name ?? "",
      assignee
        ? getDisplayName(assignee.firstName, assignee.lastName, assignee.email)
        : "",
      priorityMeta(row.priority).label,
      shortDate(row.date),
      shortDate(row.plannedDate),
      shortDate(row.plannedEndDate),
      labels,
      row.overdue ? "Yes" : "",
      row.subtaskTotal > 0 ? row.subtaskCompleted : "",
      row.subtaskTotal > 0 ? row.subtaskTotal : "",
      row.notes ?? "",
    ];
  });

  return {
    title: title ?? "Tasks",
    sheets: [
      {
        name: "Tasks",
        headers,
        rows: bodyRows,
      },
    ],
  };
}
